import type { AddSourceTableRequest } from '@/api/control/missionImpactAPI.schemas';
import type { SelectedTableConfig } from '@/lib/sourceTableSelection';

/**
 * Maximum number of add-source-table requests in flight at once. Bounds the load a large
 * "select all" batch (hundreds of tables) puts on the API, the Auth Service access checks and the
 * database, while still finishing a big batch in reasonable time.
 */
export const SOURCE_TABLE_CREATE_CONCURRENCY = 6;

/**
 * Number of failed table names listed per failure reason before collapsing the rest into a count.
 */
export const MAX_FAILED_NAMES_PER_REASON = 5;

/**
 * Builds the polymorphic add-source-table payload for one selected table. The presence of
 * `apiEndpoint` identifies a Business Central table; otherwise the parent source type decides
 * between Salesforce and Dataverse, so the backend `$type` discriminator routes correctly.
 */
export function buildAddSourceTableRequest(
  logicalName: string,
  config: SelectedTableConfig,
  sourceType: string,
): AddSourceTableRequest {
  const trimmedName = logicalName.trim();

  if (config.apiEndpoint) {
    return {
      $type: 'BusinessCentral',
      logicalName: trimmedName,
      displayName: config.displayName,
      isEnabled: true,
      apiEndpoint: config.apiEndpoint,
      timestampField: config.timestampField,
      hasIsDeletedField: config.hasIsDeletedField,
      isDeletedFieldName: config.isDeletedFieldName,
    };
  }

  if (sourceType === 'Salesforce') {
    return { $type: 'Salesforce', logicalName: trimmedName, displayName: config.displayName, isEnabled: true };
  }

  return { $type: 'Dataverse', logicalName: trimmedName, displayName: config.displayName, isEnabled: true };
}

/**
 * A table that failed to be added, with the user-facing reason.
 */
export interface FailedTable {
  logicalName: string;
  message: string;
}

/**
 * Failed tables sharing one reason, for compact display.
 */
export interface FailureGroup {
  message: string;
  /** Up to {@link MAX_FAILED_NAMES_PER_REASON} logical names, in batch order. */
  names: string[];
  /** Number of further failed tables with this reason that are not listed in `names`. */
  hiddenCount: number;
}

/**
 * Groups failed tables by reason (in order of first occurrence), listing a bounded number of names
 * per reason so a batch of hundreds of failures stays readable.
 */
export function groupFailures(failures: readonly FailedTable[]): FailureGroup[] {
  const byMessage = new Map<string, string[]>();
  for (const failure of failures) {
    const names = byMessage.get(failure.message) ?? [];
    names.push(failure.logicalName);
    byMessage.set(failure.message, names);
  }

  return Array.from(byMessage, ([message, names]) => ({
    message,
    names: names.slice(0, MAX_FAILED_NAMES_PER_REASON),
    hiddenCount: Math.max(0, names.length - MAX_FAILED_NAMES_PER_REASON),
  }));
}

/**
 * Returns a copy of the selection containing only the given logical names. Used after a partial
 * failure so a retry covers exactly the tables that were not added.
 */
export function retainSelection(
  selection: ReadonlyMap<string, SelectedTableConfig>,
  logicalNames: readonly string[],
): Map<string, SelectedTableConfig> {
  const next = new Map<string, SelectedTableConfig>();
  for (const name of logicalNames) {
    const config = selection.get(name);
    if (config) {
      next.set(name, config);
    }
  }
  return next;
}
